import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  CircularProgress,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import WordTimingEditor from './WordTimingEditor';

// Word-by-word diff highlighting
function highlightDifferences(original, corrected) {
  const origWords = original.split(/(\s+)/);
  const corrWords = corrected.split(/(\s+)/);

  const result = { original: [], corrected: [] };
  const maxLen = Math.max(origWords.length, corrWords.length);

  for (let i = 0; i < maxLen; i++) {
    const origWord = origWords[i] || '';
    const corrWord = corrWords[i] || '';

    if (origWord !== corrWord) {
      if (origWord) result.original.push({ text: origWord, highlight: true });
      if (corrWord) result.corrected.push({ text: corrWord, highlight: true });
    } else {
      if (origWord) result.original.push({ text: origWord, highlight: false });
      if (corrWord) result.corrected.push({ text: corrWord, highlight: false });
    }
  }

  return result;
}

function handleDownload(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Step 3 – Review & bewerk ondertiteling.
 */
function StepReview({
  feedback,
  corrections,
  correctionStatus, setCorrectionStatus,
  correctionEdits, setCorrectionEdits,
  editableSRT, setEditableSRT,
  generatedSRT,
  videoPreviewUrl,
  videoRef,
  playbackTime, setPlaybackTime,
  wordTimestamps,
  showWordEditor, setShowWordEditor,
  aspectRatio,
  getBaseFilename,
  uploadToFrameIo,
  isRendering,
  handleBack,
  handleRender,
  progress,
  progressMessage,
  jobId,
  handleRealign,
  isRealigning,
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6">✏️ Review &amp; bewerk ondertiteling</Typography>
        <Typography variant="body2">
          Controleer de gegenereerde ondertiteling hieronder.
          Je kunt aanpassingen maken voordat de video wordt gerenderd.
        </Typography>
      </Alert>

      {feedback && (
        <Card sx={{ mb: 3, backgroundColor: '#fff3e0' }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              💡 Feedback van AI-controle:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {feedback}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* AI Corrections Review */}
      {corrections.length > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoFixHighIcon color="primary" />
              AI-correcties ({corrections.length}) - Keur per stuk goed of af
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Klik op ✅ om een correctie toe te passen, of ❌ om te weigeren.
            </Typography>

            {corrections.map((correction, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor:
                    correctionStatus[idx] === 'accepted'
                      ? '#e8f5e9'
                      : correctionStatus[idx] === 'rejected'
                      ? '#ffebee'
                      : '#fff',
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Typography variant="caption" color="textSecondary">
                      Blok {correction.block} - Origineel:
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        textDecoration: correctionStatus[idx] === 'accepted' ? 'line-through' : 'none',
                        color: correctionStatus[idx] === 'accepted' ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      &quot;{highlightDifferences(correction.original, correction.corrected).original.map((part, i) => (
                        <span
                          key={i}
                          style={{
                            backgroundColor: part.highlight ? '#ffcdd2' : 'transparent',
                            borderRadius: part.highlight ? '2px' : '0',
                            padding: part.highlight ? '0 2px' : '0',
                          }}
                        >
                          {part.text}
                        </span>
                      ))}&quot;
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Typography variant="caption" color="textSecondary">Correctie:</Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        fontWeight: correctionStatus[idx] === 'accepted' ? 'bold' : 'normal',
                        color: correctionStatus[idx] === 'accepted' ? 'success.main' : 'text.primary',
                      }}
                    >
                      &quot;{highlightDifferences(correction.original, correction.corrected).corrected.map((part, i) => (
                        <span
                          key={i}
                          style={{
                            backgroundColor: part.highlight ? '#c8e6c9' : 'transparent',
                            borderRadius: part.highlight ? '2px' : '0',
                            padding: part.highlight ? '0 2px' : '0',
                            fontWeight: part.highlight ? 'bold' : 'inherit',
                          }}
                        >
                          {part.text}
                        </span>
                      ))}&quot;
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      💡 {correction.reason}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Accepteren">
                        <IconButton
                          color={correctionStatus[idx] === 'accepted' ? 'success' : 'default'}
                          onClick={() => {
                            setCorrectionStatus((prev) => ({ ...prev, [idx]: 'accepted' }));
                            setEditableSRT((prev) => prev.replace(correction.original, correction.corrected));
                          }}
                          disabled={correctionStatus[idx] === 'accepted'}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bewerken">
                        <IconButton
                          color={correctionStatus[idx] === 'editing' ? 'primary' : 'default'}
                          onClick={() => {
                            setCorrectionStatus((prev) => ({ ...prev, [idx]: 'editing' }));
                            setCorrectionEdits((prev) => ({ ...prev, [idx]: correction.corrected }));
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Afwijzen">
                        <IconButton
                          color={correctionStatus[idx] === 'rejected' ? 'error' : 'default'}
                          onClick={() => {
                            setCorrectionStatus((prev) => ({ ...prev, [idx]: 'rejected' }));
                            if (correctionStatus[idx] === 'accepted') {
                              setEditableSRT((prev) => prev.replace(correction.corrected, correction.original));
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>

                {/* Inline edit field */}
                {correctionStatus[idx] === 'editing' && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth multiline size="small"
                      value={correctionEdits[idx] || correction.corrected}
                      onChange={(e) => setCorrectionEdits((prev) => ({ ...prev, [idx]: e.target.value }))}
                      sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.9rem' } }}
                    />
                    <Button
                      variant="contained" size="small" color="success"
                      onClick={() => {
                        const editedText = correctionEdits[idx] || correction.corrected;
                        setEditableSRT((prev) => prev.replace(correction.original, editedText));
                        setCorrectionStatus((prev) => ({ ...prev, [idx]: 'accepted' }));
                      }}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      Toepassen
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="outlined" color="success" size="small"
                onClick={() => {
                  corrections.forEach((correction, idx) => {
                    if (correctionStatus[idx] !== 'accepted') {
                      setCorrectionStatus((prev) => ({ ...prev, [idx]: 'accepted' }));
                      setEditableSRT((prev) => prev.replace(correction.original, correction.corrected));
                    }
                  });
                }}
              >
                ✅ Alle accepteren
              </Button>
              <Button
                variant="outlined" color="error" size="small"
                onClick={() => {
                  const newStatus = {};
                  corrections.forEach((_, idx) => { newStatus[idx] = 'rejected'; });
                  setCorrectionStatus(newStatus);
                  setEditableSRT(generatedSRT || '');
                }}
              >
                ❌ Alle afwijzen
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Audio/Video Player for verification */}
      {videoPreviewUrl && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🎧 Audio afspelen om correcties te verifiëren
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Luister naar het fragment om te controleren of de AI-suggesties kloppen met wat er gezegd wordt.
          </Typography>
          <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2, border: '1px solid #e0e0e0' }}>
            <video
              ref={videoRef}
              controls
              src={videoPreviewUrl}
              onTimeUpdate={(e) => setPlaybackTime(e.target.currentTime)}
              onPause={() => setPlaybackTime(null)}
              onEnded={() => setPlaybackTime(null)}
              style={{ width: '100%', maxHeight: '200px', borderRadius: '8px', backgroundColor: '#000' }}
            >
              Je browser ondersteunt geen video playback.
            </video>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            📝 SRT Ondertiteling ({getBaseFilename()}.srt)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {wordTimestamps && wordTimestamps.length > 0 && (
              <Button
                size="small"
                variant={showWordEditor ? 'contained' : 'outlined'}
                startIcon={showWordEditor ? <EditIcon /> : <SubtitlesIcon />}
                onClick={() => setShowWordEditor(!showWordEditor)}
              >
                {showWordEditor ? 'Tekst bewerken' : 'Timing aanpassen'}
              </Button>
            )}
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(editableSRT, `${getBaseFilename()}.srt`)}
            >
              Download SRT
            </Button>
          </Box>
        </Box>

        {showWordEditor && wordTimestamps && wordTimestamps.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Timing modus:</strong> Splits, merge of verplaats woorden tussen regels.
                Schakel naar <strong>"Tekst bewerken"</strong> om de tekst zelf aan te passen.
              </Typography>
            </Alert>
            <WordTimingEditor
              srtContent={editableSRT}
              wordTimestamps={wordTimestamps}
              onChange={(newSRT) => setEditableSRT(newSRT)}
              currentTime={playbackTime}
              maxCharsPerLine={aspectRatio === '9:16' ? 25 : aspectRatio === '1:1' ? 38 : 42}
            />
          </>
        ) : (
          <TextField
            fullWidth multiline rows={15}
            value={editableSRT}
            onChange={(e) => setEditableSRT(e.target.value)}
            placeholder="SRT ondertiteling..."
            sx={{
              fontFamily: 'monospace',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                lineHeight: 1.5,
              },
            }}
          />
        )}
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Tip: SRT format: nummer, timing (00:00:00,000 --&gt; 00:00:00,000), tekst
        </Typography>
      </Paper>

      {uploadToFrameIo && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            ⚠️ Na goedkeuring wordt de video gerenderd met ingebakken ondertiteling en geüpload naar Frame.io.
            Dit kan enkele minuten duren.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleBack} disabled={isRendering || isRealigning}>
          Terug
        </Button>
        {jobId && handleRealign && (
          <Button
            variant="outlined"
            onClick={handleRealign}
            disabled={isRendering || isRealigning || !editableSRT.trim()}
            startIcon={isRealigning ? <CircularProgress size={18} /> : null}
            title="Match de tekst opnieuw tegen de originele audio-transcriptie en herbereken start/eind per cue"
          >
            {isRealigning ? 'Timing herberekenen…' : 'Timing opnieuw controleren'}
          </Button>
        )}
        <Button
          variant="contained" color="primary"
          onClick={handleRender}
          disabled={isRendering || isRealigning || !editableSRT.trim()}
          startIcon={isRendering ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
        >
          {isRendering ? 'Renderen...' : (uploadToFrameIo ? 'Goedkeuren & Renderen' : 'Goedkeuren')}
        </Button>
      </Box>

      {isRendering && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {progressMessage || 'Video renderen met ondertiteling...'}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 2, maxWidth: 400, mx: 'auto', height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {progress}% voltooid
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default StepReview;
